import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { Account, GroupAccountDirectBalance, JournalEntry } from '../../interfaces/accounting.interface';
import { GroupAccountReclassService } from '../../services/groupAccountReclass.service';
import { AccountService } from '../../services/account.service';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { leafAccounts } from '../../utils/postingAccounts';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

/** True when `account` sits anywhere under `group` — by parent link or by 10-digit code prefix. */
export const isDescendantOf = (
    account: Account,
    group: Pick<GroupAccountDirectBalance, 'accountId' | 'code' | 'descendantPrefix'>,
    byId: Map<string, Account>,
): boolean => {
    if (group.descendantPrefix && account.code !== group.code && account.code?.startsWith(group.descendantPrefix)) {
        return true;
    }
    let parentId = account.parentAccountId;
    const seen = new Set<string>();
    while (parentId && !seen.has(parentId)) {
        if (parentId === group.accountId) return true;
        seen.add(parentId);
        parentId = byId.get(parentId)?.parentAccountId;
    }
    return false;
};

/**
 * Posting accounts a group's balance may move to: same type, leaf only, not the group itself.
 * Sub-accounts of the group come first (the usual right answer), then the rest of that type.
 */
export const targetOptionsFor = (group: GroupAccountDirectBalance, accounts: Account[]): Account[] => {
    const byId = new Map(accounts.filter(a => a.id).map(a => [a.id as string, a]));
    const candidates = leafAccounts(accounts)
        .filter(a => a.id && a.id !== group.accountId && a.accountType === group.accountType)
        // A code-prefix parent (no parentAccountId link) is still a group account on the server.
        .filter(a => !accounts.some(b => {
            if (b.id === a.id || !b.code || !a.code || a.code.length !== 10) return false;
            let end = 10;
            while (end > 2 && a.code.slice(end - 2, end) === '00') end -= 2;
            return end < 10 && b.code !== a.code && b.code.startsWith(a.code.slice(0, end));
        }));
    const under = candidates.filter(a => isDescendantOf(a, group, byId));
    const rest = candidates.filter(a => !isDescendantOf(a, group, byId));
    const byCode = (x: Account, y: Account) => (x.code || '').localeCompare(y.code || '');
    return [...under.sort(byCode), ...rest.sort(byCode)];
};

const GroupAccountBalances: React.FC = () => {
    const toast = useToast();
    const canPost = usePermission(PERMISSIONS.JOURNAL_ENTRY_POST);

    const [rows, setRows] = useState<GroupAccountDirectBalance[]>([]);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [loading, setLoading] = useState(true);
    const [lastEntry, setLastEntry] = useState<JournalEntry | null>(null);

    const [selected, setSelected] = useState<GroupAccountDirectBalance | null>(null);
    const [targetId, setTargetId] = useState('');
    const [date, setDate] = useState(toLocalIsoDate());
    const [memo, setMemo] = useState('');
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [balances, allAccounts] = await Promise.all([
                GroupAccountReclassService.list(),
                AccountService.getAllAccounts(),
            ]);
            setRows(balances);
            setAccounts(allAccounts);
        } catch (err) {
            toast.showApiError(err, 'Failed to load group account balances');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    const options = useMemo(() => (selected ? targetOptionsFor(selected, accounts) : []), [selected, accounts]);
    const descendantCount = useMemo(() => {
        if (!selected) return 0;
        const byId = new Map(accounts.filter(a => a.id).map(a => [a.id as string, a]));
        return options.filter(a => isDescendantOf(a, selected, byId)).length;
    }, [selected, options, accounts]);

    const openDialog = (row: GroupAccountDirectBalance) => {
        setSelected(row);
        setTargetId('');
        setDate(toLocalIsoDate());
        setMemo('');
    };

    const closeDialog = () => {
        if (!saving) setSelected(null);
    };

    const handleSubmit = async () => {
        if (!selected || !targetId || !memo.trim() || !date) return;
        setSaving(true);
        try {
            const entry = await GroupAccountReclassService.reclassify({
                groupAccountId: selected.accountId,
                targetAccountId: targetId,
                date,
                memo: memo.trim(),
            });
            toast.showSuccess(`Balance of ${selected.code} moved${entry.entryNumber ? ` in ${entry.entryNumber}` : ''}`);
            setLastEntry(entry);
            setSelected(null);
            await load();
        } catch (err) {
            toast.showApiError(err, 'Failed to reclassify the group account balance');
        } finally {
            setSaving(false);
        }
    };

    const selectedTarget = accounts.find(a => a.id === targetId);

    return (
        <Box data-help-page="accounting.groupBalances.page" sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h5" gutterBottom>Group Account Balances</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Group accounts only add up their sub-accounts, but some carry amounts posted straight to them in the past.
                Move each of those amounts to the right sub-account here — it posts one journal entry per group.
            </Typography>

            {lastEntry?.id && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setLastEntry(null)}>
                    Posted{' '}
                    <Link component={RouterLink} to={`/accounting/journal-entries/${lastEntry.id}`}>
                        {lastEntry.entryNumber || 'the journal entry'}
                    </Link>
                    .
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : rows.length === 0 ? (
                <Alert severity="info">No group account carries a balance of its own. Nothing to fix.</Alert>
            ) : (
                <TableContainer data-help-section="accounting.groupBalances.table" component={Paper} sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="accounting.groupBalances.col.account">Group account</TableCell>
                                <TableCell data-help="accounting.groupBalances.col.type">Type</TableCell>
                                <TableCell data-help="accounting.groupBalances.col.balance" align="right">Own balance</TableCell>
                                <TableCell data-help="accounting.groupBalances.col.lines" align="right">Lines</TableCell>
                                <TableCell data-help="accounting.groupBalances.col.action" align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(row => (
                                <TableRow key={row.accountId} data-testid={`group-row-${row.code}`}>
                                    <TableCell>
                                        <Link data-help="accounting.groupBalances.action.ledger" component={RouterLink} to={`/accounting/accounts/${row.accountId}/ledger`}>
                                            {row.code} - {row.name}
                                        </Link>
                                    </TableCell>
                                    <TableCell>{humanizeEnum(row.accountType)}</TableCell>
                                    <TableCell align="right">
                                        {formatMoney(row.amount)} {row.balanceSide === 'DEBIT' ? 'Dr' : 'Cr'}
                                    </TableCell>
                                    <TableCell align="right">{row.lineCount}</TableCell>
                                    <TableCell align="right">
                                        <Button data-help="accounting.groupBalances.action.reclassify" size="small" variant="outlined" disabled={!canPost} onClick={() => openDialog(row)}>
                                            Reclassify
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog data-help-page="accounting.groupBalances.reclass.page" open={!!selected} onClose={closeDialog} fullWidth maxWidth="sm">
                <DialogTitle>Reclassify {selected?.code} - {selected?.name}</DialogTitle>
                <DialogContent>
                    {selected && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <Typography variant="body2">
                                Moves the whole own balance of{' '}
                                <strong>{formatMoney(selected.amount)} {selected.balanceSide === 'DEBIT' ? 'Dr' : 'Cr'}</strong>{' '}
                                to the posting account you choose. The group ends at zero.
                            </Typography>
                            <AccountAutocomplete data-help="accounting.groupBalances.reclass.field.target"
                                accounts={options}
                                value={targetId}
                                onChange={setTargetId}
                                label="Move to account"
                                required
                                fullWidth
                                helperText={descendantCount > 0
                                    ? `Sub-accounts of ${selected.code} are listed first; other ${humanizeEnum(selected.accountType).toLowerCase()} accounts follow.`
                                    : `Only ${humanizeEnum(selected.accountType).toLowerCase()} posting accounts can receive this balance.`}
                                data-testid="reclass-target"
                            />
                            {selectedTarget && descendantCount > 0 && !isDescendantOf(
                                selectedTarget, selected,
                                new Map(accounts.filter(a => a.id).map(a => [a.id as string, a])),
                            ) && (
                                <Alert severity="warning">
                                    {selectedTarget.code} is not under {selected.code}. Check this is the account you mean.
                                </Alert>
                            )}
                            <TextField data-help="accounting.groupBalances.reclass.field.date"
                                label="Date"
                                type="date"
                                value={date}
                                onChange={e => setDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                helperText="Must be in an open period"
                                required
                                fullWidth
                            />
                            <TextField data-help="accounting.groupBalances.reclass.field.memo"
                                label="Memo"
                                value={memo}
                                onChange={e => setMemo(e.target.value)}
                                placeholder="e.g. Expense payments posted to the ASSETS header before the guard"
                                inputProps={{ maxLength: 150 }}
                                required
                                fullWidth
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button data-help="accounting.groupBalances.reclass.cancel" onClick={closeDialog} disabled={saving}>Cancel</Button>
                    <Button data-help="accounting.groupBalances.reclass.submit"
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={saving || !targetId || !memo.trim() || !date}
                    >
                        {saving ? 'Posting…' : 'Post reclassification'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default GroupAccountBalances;
